/**
 * DiffView — docking panel for comparing two branches side-by-side.
 *
 * Uses a simple table layout (not AG Grid, to keep it lightweight).
 * Color-coded rows: green=added, yellow=modified, red=deleted.
 */

import { useState } from "react";
import { Badge, Box, Button, Flex, Heading, ScrollArea, Select, Text } from "@radix-ui/themes";

import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useBranches, useDiffBranches } from "../../hooks/useVersioning";

const STATUS_COLORS: Record<string, string> = {
  only_a: "var(--red-a3)",
  only_b: "var(--green-a3)",
  modified: "var(--yellow-a3)",
  unchanged: "transparent",
};

const STATUS_LABELS: Record<string, { label: string; color: "red" | "green" | "yellow" | "gray" }> = {
  only_a: { label: "Deleted", color: "red" },
  only_b: { label: "Added", color: "green" },
  modified: { label: "Modified", color: "yellow" },
  unchanged: { label: "Unchanged", color: "gray" },
};

export function DiffView() {
  const { projectId, currentBranch } = useProvenanceContext();
  const { data: branches } = useBranches(projectId);

  const [branchA, setBranchA] = useState(currentBranch);
  const [branchB, setBranchB] = useState("main");
  const [enabled, setEnabled] = useState(false);

  const { data: diff, isLoading } = useDiffBranches(
    projectId,
    branchA,
    branchB,
    enabled,
  );

  const changedRows = diff?.filter((d) => d.status !== "unchanged") ?? [];

  return (
    <Box p="3" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Heading size="3" mb="3">Branch Diff</Heading>

      <Flex gap="2" align="end" mb="3" wrap="wrap">
        <Flex direction="column" gap="1">
          <Text size="1" color="gray">Branch A</Text>
          <Select.Root value={branchA} onValueChange={setBranchA}>
            <Select.Trigger />
            <Select.Content>
              {branches?.map((b) => (
                <Select.Item key={b.id} value={b.name}>{b.name}</Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>

        <Text size="2" color="gray" style={{ paddingBottom: 6 }}>vs</Text>

        <Flex direction="column" gap="1">
          <Text size="1" color="gray">Branch B</Text>
          <Select.Root value={branchB} onValueChange={setBranchB}>
            <Select.Trigger />
            <Select.Content>
              {branches?.map((b) => (
                <Select.Item key={b.id} value={b.name}>{b.name}</Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>

        <Button
          size="2"
          onClick={() => setEnabled(true)}
          disabled={branchA === branchB}
        >
          Compare
        </Button>
      </Flex>

      {isLoading && <Text size="2" color="gray">Computing diff...</Text>}

      {diff && (
        <Text size="1" color="gray" mb="2">
          {changedRows.length} change{changedRows.length !== 1 ? "s" : ""} found
          {diff.length > changedRows.length &&
            ` (${diff.length - changedRows.length} unchanged hidden)`}
        </Text>
      )}

      <ScrollArea style={{ flex: 1 }}>
        <Box asChild style={{ width: "100%", borderCollapse: "collapse" }}>
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--gray-a4)" }}>
                  <Text size="1" weight="medium">Path</Text>
                </th>
                <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--gray-a4)" }}>
                  <Text size="1" weight="medium">Status</Text>
                </th>
                <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--gray-a4)" }}>
                  <Text size="1" weight="medium">Value A</Text>
                </th>
                <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--gray-a4)" }}>
                  <Text size="1" weight="medium">Value B</Text>
                </th>
              </tr>
            </thead>
            <tbody>
              {changedRows.map((entry) => {
                const status = (STATUS_LABELS[entry.status] ?? STATUS_LABELS.unchanged)!;
                return (
                  <tr
                    key={entry.path}
                    style={{ background: STATUS_COLORS[entry.status] }}
                  >
                    <td style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-a3)" }}>
                      <Text size="1" style={{ fontFamily: "var(--code-font-family, monospace)" }}>
                        {entry.path}
                      </Text>
                    </td>
                    <td style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-a3)" }}>
                      <Badge size="1" color={status.color}>{status.label}</Badge>
                    </td>
                    <td style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-a3)" }}>
                      <Text size="1" style={{ fontFamily: "var(--code-font-family, monospace)" }}>
                        {entry.value_a_json != null ? JSON.stringify(entry.value_a_json) : "—"}
                      </Text>
                    </td>
                    <td style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-a3)" }}>
                      <Text size="1" style={{ fontFamily: "var(--code-font-family, monospace)" }}>
                        {entry.value_b_json != null ? JSON.stringify(entry.value_b_json) : "—"}
                      </Text>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </ScrollArea>
    </Box>
  );
}
